import React, { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Dialog, Transition } from '@headlessui/react';
import toast from 'react-hot-toast';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ReplayIcon from '@mui/icons-material/Replay';
import {
  admissionError,
  useAdmission,
  useReplay,
} from '../../hooks/useAdmissions';
import { canViewStudentIntelligence } from '../../utils/permissionKeys';
import StatusBadge from './StatusBadge';
import {
  OUTCOME_LABELS,
  REASON_FIXES,
  STATUS_HELP,
  cohortLabel,
  formatDateTime,
  reasonLabel,
} from './admissionText';

function Fact({ label, children }) {
  return (
    <div className="grid grid-cols-[8rem_1fr] gap-3 py-1.5 text-sm">
      <dt className="text-gray-500">{label}</dt>
      <dd className="text-gray-900 break-words min-w-0">{children}</dd>
    </div>
  );
}

function IdButton({ id, onOpen, children }) {
  return (
    <button
      type="button"
      onClick={() => onOpen(id)}
      className="text-claret-500 hover:underline"
    >
      {children}
    </button>
  );
}

function ExternalLink({ href, children }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
    >
      {children}
      <OpenInNewIcon sx={{ fontSize: 14 }} />
    </a>
  );
}

// Why it failed and what to do about it.
function Problem({ admission }) {
  const { status } = admission;
  if (status === 'failed') {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-3 space-y-1">
        <p className="text-sm font-medium text-red-800">
          {reasonLabel(admission) || 'Not admitted.'}
        </p>
        {admission.detail && (
          <p className="text-xs text-red-800">{admission.detail}</p>
        )}
        {REASON_FIXES[admission.reason] && (
          <p className="text-xs text-red-700">
            {REASON_FIXES[admission.reason]}
          </p>
        )}
      </div>
    );
  }
  if (status === 'dead_lettered' || status === 'retrying') {
    const tone =
      status === 'retrying'
        ? 'border-amber-200 bg-amber-50 text-amber-800'
        : 'border-red-200 bg-red-50 text-red-800';
    return (
      <div className={`rounded-lg border p-3 space-y-1 ${tone}`}>
        <p className="text-sm font-medium">{STATUS_HELP[status]}</p>
        {admission.last_error && (
          <p className="text-xs font-mono break-words">
            {admission.last_error}
          </p>
        )}
        {status === 'dead_lettered' && (
          <p className="text-xs">
            Check the cause (often forms or the LMS was down), then replay.
          </p>
        )}
      </div>
    );
  }
  return null;
}

function ReplayPanel({ admission, enabled, onOpen }) {
  const replay = useReplay();
  const [confirming, setConfirming] = useState(false);
  const [conflictId, setConflictId] = useState(null);

  useEffect(() => {
    setConfirming(false);
    setConflictId(null);
  }, [admission.id]);

  if (!admission.can_replay && !conflictId) return null;

  const run = () =>
    replay.mutate(admission.id, {
      onSuccess: (created) => {
        toast.success('Replay started.');
        setConfirming(false);
        if (created?.id) onOpen(created.id);
      },
      onError: (error) => {
        setConfirming(false);
        const replayId = error?.response?.data?.replay_id;
        if (error?.response?.status === 409 && replayId) {
          setConflictId(replayId);
          return;
        }
        toast.error(admissionError(error, "We couldn't start the replay."));
      },
    });

  if (conflictId) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
        This admission already has a replay in progress or admitted.{' '}
        <IdButton id={conflictId} onOpen={onOpen}>
          Open that replay
        </IdButton>
      </div>
    );
  }

  if (!enabled) {
    return (
      <p className="text-xs text-gray-500">
        Replay is unavailable while admissions from forms are switched off.
      </p>
    );
  }

  if (!confirming) {
    return (
      <button
        type="button"
        onClick={() => setConfirming(true)}
        className="inline-flex items-center gap-1 rounded-md bg-claret-500 px-4 py-2 text-sm text-white hover:bg-claret-400"
      >
        <ReplayIcon fontSize="small" />
        Replay
      </button>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 p-3 space-y-3">
      <p className="text-sm text-gray-700">
        Fix the cause first. Replaying fetches the applicant again from forms
        and admits them to the same cohort.
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={run}
          disabled={replay.isLoading}
          className="rounded-md bg-claret-500 px-4 py-1.5 text-sm text-white hover:bg-claret-400 disabled:opacity-60"
        >
          {replay.isLoading ? 'Starting…' : 'Replay now'}
        </button>
        <button
          type="button"
          onClick={() => setConfirming(false)}
          className="rounded-md border border-gray-300 px-4 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function Body({ data, enabled, onOpen }) {
  const permissions = useSelector((state) => state.auth.permissions ?? []);
  const role = useSelector((state) => state.auth.userDetails?.role);
  const { admission, replays = [], attempt_log: attempts = [] } = data;
  const { applicant, form } = admission;

  return (
    <div className="space-y-5">
      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={admission.status} />
          {admission.replayed_from && (
            <span className="text-[11px] text-gray-500">Replay</span>
          )}
        </div>
        {admission.status === 'admitted' && admission.outcome && (
          <p className="text-sm text-gray-700">
            {OUTCOME_LABELS[admission.outcome] || admission.outcome}
          </p>
        )}
      </div>

      {admission.superseded_by && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
          This admission has been replayed.{' '}
          <IdButton id={admission.superseded_by} onOpen={onOpen}>
            Open the latest attempt
          </IdButton>
        </div>
      )}

      <Problem admission={admission} />
      <ReplayPanel admission={admission} enabled={enabled} onOpen={onOpen} />

      {(form?.submission_url || form?.collection_url) && (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            {form.submission_url && (
              <ExternalLink href={form.submission_url}>
                Open submission on forms
              </ExternalLink>
            )}
            {form.collection_url && (
              <ExternalLink href={form.collection_url}>
                {form.collection_name || 'Open collection on forms'}
              </ExternalLink>
            )}
          </div>
          <p className="text-[11px] text-gray-500">
            Forms links need your HMS Keycloak sign-in.
          </p>
        </div>
      )}

      <dl className="divide-y divide-gray-100 border-y border-gray-100">
        <Fact label="Applicant">
          {applicant?.name || applicant?.email ? (
            <>
              {applicant.name || '–'}
              {applicant.email && (
                <span className="block text-xs text-gray-500">
                  {applicant.email}
                </span>
              )}
            </>
          ) : (
            <span className="text-gray-500">Not fetched from forms yet</span>
          )}
        </Fact>
        <Fact label="Cohort">{cohortLabel(admission.cohort)}</Fact>
        {form?.collection_name && (
          <Fact label="Collection">{form.collection_name}</Fact>
        )}
        <Fact label="Received">{formatDateTime(admission.received_at)}</Fact>
        <Fact label="Processed">{formatDateTime(admission.processed_at)}</Fact>
        <Fact label="Attempts">{admission.attempts ?? '–'}</Fact>
        {admission.lms_student_id && (
          <Fact label="LMS student">
            {canViewStudentIntelligence(permissions, role) ? (
              <Link
                to={`/users/student/${admission.lms_student_id}`}
                className="text-claret-500 hover:underline"
              >
                Open student profile
              </Link>
            ) : (
              <span className="font-mono text-xs">
                {admission.lms_student_id}
              </span>
            )}
          </Fact>
        )}
        {admission.replayed_from && (
          <Fact label="Replay of">
            <IdButton id={admission.replayed_from} onOpen={onOpen}>
              Open the original
            </IdButton>
            {(admission.replayed_by?.name || admission.replayed_at) && (
              <span className="block text-xs text-gray-500">
                {[
                  admission.replayed_by?.name &&
                    `By ${admission.replayed_by.name}`,
                  admission.replayed_at &&
                    formatDateTime(admission.replayed_at),
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            )}
          </Fact>
        )}
        <Fact label="Submission">
          <span className="font-mono text-xs">
            {admission.submission_id || '–'}
          </span>
        </Fact>
        <Fact label="Event">
          <span className="font-mono text-xs">{admission.event_id || '–'}</span>
        </Fact>
      </dl>

      {replays.length > 0 && (
        <section className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Replays
          </p>
          <ul className="m-0 list-none divide-y divide-gray-100 rounded-lg border border-gray-200 p-0">
            {replays.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => onOpen(item.id)}
                  className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-gray-50"
                >
                  <span className="text-sm text-gray-700">
                    {formatDateTime(item.replayed_at || item.received_at)}
                    {item.replayed_by?.name && (
                      <span className="block text-xs text-gray-500">
                        By {item.replayed_by.name}
                      </span>
                    )}
                  </span>
                  <StatusBadge status={item.status} />
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {attempts.length > 0 && (
        <section className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Failed attempts
          </p>
          <ul className="m-0 list-none space-y-1.5 p-0">
            {attempts.map((attempt, index) => (
              // Attempts have no id; the list is fixed for one response.
              // eslint-disable-next-line react/no-array-index-key
              <li key={`${attempt.at}-${index}`} className="text-xs">
                <span className="text-gray-500">
                  {formatDateTime(attempt.at)}
                </span>
                <span className="block font-mono text-gray-700 break-words">
                  {attempt.error}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

export default function AdmissionDrawer({ id, enabled, onOpen, onClose }) {
  const { data, isLoading, error, refetch } = useAdmission(id);
  const title =
    data?.admission?.applicant?.name ||
    data?.admission?.applicant?.email ||
    'Admission';

  let content;
  if (isLoading) {
    content = (
      <div className="space-y-3" aria-label="Loading">
        <div className="h-6 w-32 rounded bg-gray-100 animate-pulse" />
        <div className="h-40 rounded bg-gray-100 animate-pulse" />
      </div>
    );
  } else if (error) {
    content = (
      <div className="space-y-3 text-sm text-gray-600">
        <p>
          {error?.response?.status === 404 && error?.response?.data?.error
            ? 'This admission no longer exists.'
            : admissionError(error, "We couldn't load this admission.")}
        </p>
        <button
          type="button"
          onClick={() => refetch()}
          className="rounded-md bg-claret-500 px-4 py-1.5 text-sm text-white hover:bg-claret-400"
        >
          Retry
        </button>
      </div>
    );
  } else if (data?.admission) {
    content = <Body data={data} enabled={enabled} onOpen={onOpen} />;
  }

  return (
    <Transition show={Boolean(id)} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>
        <div className="fixed inset-y-0 right-0 flex max-w-full">
          <Transition.Child
            as={Fragment}
            enter="transform transition ease-out duration-200"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="transform transition ease-in duration-150"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
          >
            <Dialog.Panel className="flex h-full w-screen max-w-lg flex-col bg-white shadow-xl">
              <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4">
                <div className="min-w-0">
                  <Dialog.Title className="truncate text-base font-semibold text-gray-900">
                    {title}
                  </Dialog.Title>
                  {data?.admission?.applicant?.name &&
                    data.admission.applicant.email && (
                      <p className="truncate text-xs text-gray-500">
                        {data.admission.applicant.email}
                      </p>
                    )}
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close"
                  className="rounded-full p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                >
                  <CloseIcon fontSize="small" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-5 py-4">{content}</div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
